import React, {ReactElement, useMemo} from 'react';
import Box from '@mui/material/Box';
import {useAppSelector} from '../../hooks';
import {selectBannerFillType} from '../../slices/storesSlice';
import {useGetStoreDetailsQuery} from '../../api/storesApi';
import './index.scss';

const imageStyle = (imgUrl: string) => ({
  backgroundImage: `url(${imgUrl})`
});

type StoreBannerProps = {
  storeSlug: string;
  hideAlert?: boolean;
};

const StoreBanner: React.FC<StoreBannerProps> = ({
  storeSlug,
  hideAlert
}: StoreBannerProps): ReactElement => {
  useGetStoreDetailsQuery(storeSlug);
  const storeDetails = useAppSelector(state => state.stores.storeDetails);
  const pendingDetails = useAppSelector(state => state.stores.pendingDetails);
  const localImages = useAppSelector(state => state.stores.localImages);
  const heroImageUrl = localImages.heroImage || storeDetails?.heroImageUrl;
  const logoImageUrl = localImages.logo || storeDetails?.logoImageUrl;
  const currentFillType = useAppSelector(selectBannerFillType);
  const storeDetailsName = storeDetails?.name;
  const bannerEnabled =
    !hideAlert &&
    (pendingDetails?.bannerEnabled !== undefined
      ? pendingDetails.bannerEnabled
      : storeDetails?.bannerEnabled);

  const bannerText =
    pendingDetails?.bannerText !== undefined
      ? pendingDetails.bannerText
      : storeDetails?.bannerText;

  const bannerPreview = useMemo(() => {
    if (currentFillType === 'image' && heroImageUrl)
      return (
        <div
          className="store-layout-banner-hero"
          style={imageStyle(heroImageUrl)}
        />
      );
    if (currentFillType === 'none' && logoImageUrl)
      return (
        <div
          className="store-layout-banner-logo"
          style={imageStyle(logoImageUrl)}
        />
      );

    // In the case no images are present for the corresponding fill types, fallback to rendering the store name
    return (
      <div className="store-layout-banner-hero-text">{storeDetailsName}</div>
    );
  }, [currentFillType, heroImageUrl, logoImageUrl, storeDetailsName]);

  return (
    <>
      <Box className="store-layout-banner">
        <div className="store-layout-banner-container">{bannerPreview}</div>
      </Box>
      {bannerEnabled && bannerText && (
        <div className="alertText">{bannerText}</div>
      )}
    </>
  );
};

StoreBanner.defaultProps = {
  hideAlert: false
};

export default StoreBanner;
